import axios from 'axios';
import { ChangeField, SupportRequestLane } from '@/constants/supportRequests';
import backendV2Service from './backend-v2.service';

/**
 * Servicio de la bandeja de aprobación (KB-907).
 *
 * `backendV2Service` decamelize automáticamente lo que se envía y camelize lo
 * que se recibe, así que acá se trabaja siempre en camelCase.
 */

export { SupportRequestLane };

export interface SupportRequestFilters {
  actionType?: string | null;
  lane?: SupportRequestLane | null;
  status?: string | null;
  schoolId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  page: number;
  perPage: number;
}

export interface TargetCandidate {
  bookuserId: number;
  fullName: string;
  dni?: string | null;
  course?: string | null;
  birthDate?: string | null;
}

export interface SupportRequestTargetItem {
  id: number;
  rawName: string | null;
  resolution: string;
  status: string;
  bookuserId: number | null;
  candidates: TargetCandidate[];
}

/** Un cambio propuesto por la gestión, en lenguaje de negocio (KB-907). */
export interface ActionChange {
  field: ChangeField;
  from: string | null;
  to: string | null;
}

export interface SupportRequestActionItem {
  id: number;
  actionType: string;
  status: string;
  lane: SupportRequestLane | null;
  payload: Record<string, unknown> | null;
  evidence: string | null;
  manualReason: string | null;
  executionWarning: string | null;
  // Opcionales (KB-907): el PR de backend que los agrega puede mergearse
  // después que este. Sin ellos la pantalla sigue funcionando como antes
  // (fallback al volcado del payload, sin alertas de verificación/diagnóstico).
  changes?: ActionChange[];
  approvalNote?: string | null;
  diagnosis?: string | null;
  undoneBy: string | null;
  undoneAt: string | null;
  version: number;
  createdAt: string;
  supportRequest: {
    id: number;
    hubspotTicketId: string;
    subject: string | null;
    resolvedSchoolId: number | null;
    resolvedSchoolName: string | null;
    ticketCreatedAt: string | null;
    replyDraft: string | null;
    // Opcional (KB-907): cuerpo completo del ticket, para el expandible.
    content?: string | null;
  };
  targets: SupportRequestTargetItem[];
}

export interface ListActionsResult {
  data: SupportRequestActionItem[];
  total: number;
  totalPages: number;
  page: number;
  perPage: number;
}

export interface ActionCounts {
  byActionType: Record<string, number>;
  byLane: Record<SupportRequestLane, number>;
  // Opcional (KB-907): el PR de backend que lo agrega puede mergearse después
  // que este. Sin él, los chips de tipo caen al `byActionType` global de
  // siempre (ver src/utils/supportRequests.ts#actionTypeCountsForLane).
  byActionTypeAndLane?: Partial<Record<SupportRequestLane, Record<string, number>>>;
}

/** Un ticket que quedó manual sin ninguna gestión propuesta (KB-907). */
export interface UnprocessedTicketItem {
  id: number;
  hubspotTicketId: string;
  subject: string | null;
  content: string | null;
  ticketCreatedAt: string | null;
  createdAt: string;
  formSchoolId: number | null;
  resolvedSchoolId: number | null;
  resolvedSchoolName: string | null;
  formStudentOrTeacherName: string | null;
  formDniOrRut: string | null;
  manualReason: string | null;
}

export interface UnprocessedFilters {
  page: number;
  // Mismos nombres que ya usa `SupportRequestFilters`/`listActions` (KB-907,
  // PR #257 de backend): perPage, dateFrom, dateTo — no limit/from/to.
  perPage: number;
  schoolId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export interface UnprocessedResult {
  items: UnprocessedTicketItem[];
  total: number;
}

/**
 * El backend devuelve 409 con un mensaje claro cuando la versión que mandó el
 * cliente ya no coincide (bloqueo optimista). Este error lo distingue para que
 * la UI muestre "esto cambió, recargá" en vez de un error genérico.
 */
export class VersionConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VersionConflictError';
  }
}

function unwrapConflict<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 409) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? 'Esta acción cambió desde que se cargó. Recargá la bandeja.';
      throw new VersionConflictError(message);
    }
    throw error;
  });
}

export default {
  listActions: (filters: SupportRequestFilters): Promise<ListActionsResult> => backendV2Service
    .get<ListActionsResult>('/support-requests/actions', { params: filters })
    .then(({ data }) => data),

  // `byActionType`/`byLane` son diccionarios cuyas CLAVES son valores de enum
  // (ACCESS_DIAGNOSE, READY_TO_APPROVE...), no snake_case real. El
  // `humps.camelizeKeys` global de `backendV2Service` las camelCasea igual
  // que cualquier property name y las destroza ("ACCESS_DIAGNOSE" ->
  // "aCCESSDIAGNOSE"), rompiendo los `$t()` de la bandeja y dejando los
  // contadores por carril siempre en 0. Como esta respuesta no tiene ningún
  // campo legítimo en snake_case, se pide sin ese paso.
  counts: (): Promise<ActionCounts> => backendV2Service
    .get<ActionCounts>('/support-requests/actions/counts', {
      transformResponse: [...axios.defaults.transformResponse as Array<(data: unknown) => unknown>],
    })
    .then(({ data }) => data),

  approve: (
    actionId: number,
    version: number,
    targetIds?: number[],
  ): Promise<SupportRequestActionItem> => unwrapConflict(
    backendV2Service
      .post<SupportRequestActionItem>(`/support-requests/actions/${actionId}/approve`, {
        version,
        targetIds,
      })
      .then(({ data }) => data),
  ),

  reject: (actionId: number, version: number): Promise<SupportRequestActionItem> => unwrapConflict(
    backendV2Service
      .post<SupportRequestActionItem>(`/support-requests/actions/${actionId}/reject`, {
        version,
      })
      .then(({ data }) => data),
  ),

  pickCandidate: (
    targetId: number,
    actionVersion: number,
    bookuserId: number,
  ): Promise<SupportRequestTargetItem> => unwrapConflict(
    backendV2Service
      .post<SupportRequestTargetItem>(`/support-requests/targets/${targetId}/pick`, {
        actionVersion,
        bookuserId,
      })
      .then(({ data }) => data),
  ),

  updateReplyDraft: (
    requestId: number,
    replyDraft: string,
  ): Promise<{ id: number; replyDraft: string | null }> => backendV2Service
    .patch<{ id: number; replyDraft: string | null }>(
      `/support-requests/requests/${requestId}/reply-draft`,
      { replyDraft },
    )
    .then(({ data }) => data),

  undo: (actionId: number, version: number): Promise<SupportRequestActionItem> => unwrapConflict(
    backendV2Service
      .post<SupportRequestActionItem>(`/support-requests/actions/${actionId}/undo`, {
        version,
      })
      .then(({ data }) => data),
  ),

  // Endpoint nuevo (KB-907): tickets del carril Manual que quedaron sin
  // ninguna gestión propuesta. Contra un backend viejo que todavía no lo
  // expone responde 404 — el llamador (SupportRequestUnprocessedList.vue) lo
  // interpreta como "ocultar la sección", no como error.
  unprocessed: (filters: UnprocessedFilters): Promise<UnprocessedResult> => backendV2Service
    .get<UnprocessedResult>('/support-requests/unprocessed', { params: filters })
    .then(({ data }) => data),
};
